import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    IconButton,
    Grid,
    CardContent,
    Card,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import dayjs from "dayjs";
import APIURL from "../api/apiClient";
import CleanupImagesButton from "../components/CleanupOfferImages";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

const OfferPage = () => {
    const navigate = useNavigate();
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [images, setImages] = useState([]);
    const [errors, setErrors] = useState({});

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        setImages((prev) => [...prev, ...files]);
    };

    const handleDeleteImage = (index) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };

    const validate = () => {
        const newErrors = {};
        if (!fromDate) newErrors.fromDate = "From date is required";
        if (!toDate) newErrors.toDate = "To date is required";
        if (fromDate && toDate && new Date(toDate) < new Date(fromDate))
            newErrors.toDate = "To date must be after From date";
        if (images.length === 0) newErrors.images = "At least one image is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const formData = new FormData();
        formData.append(
            "fromDate",
            dayjs(fromDate).startOf("day").format("YYYY-MM-DD HH:mm:ss")
        );
        formData.append(
            "toDate",
            dayjs(toDate).endOf("day").format("YYYY-MM-DD HH:mm:ss")
        );

        images.forEach((img) => formData.append("images[]", img));

        try {
            let response = await APIURL.post("/addoffers.php", formData, true);

             // If response is a string (not JSON), try to parse it
                    if (typeof response === "string") {
                        try {
                            response = JSON.parse(response.trim());
                        } catch {
                            // fallback if not JSON
                            response = { success: false, message: response };
                        }
                    }

            if (response.status) {
                message.success(response.message || "Offer added successfully!");
                setFromDate("");
                navigate('/offerbannerlists')
                setToDate("");
                setImages([]);
            }
        } catch (err) {
            console.error("Error submitting offer:", err);
            message.error("Failed to submit offer.");
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                minHeight: "100vh",
                // bgcolor: "#f5f6fa",
                py: 6,
            }}
        >
            <Card
                sx={{
                    width: "100%",
                    maxWidth: 800,
                    borderRadius: 4,
                    boxShadow:
                        "0px 8px 25px rgba(0, 0, 0, 0.1), 0px 3px 8px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                        boxShadow:
                            "0px 10px 30px rgba(0,0,0,0.15), 0px 5px 10px rgba(0,0,0,0.08)",
                        transform: "translateY(-2px)",
                    },
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    {/* Header Section */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 4,
                            borderBottom: "2px solid #e0e0e0",
                            pb: 2,
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#333" }}>
                            Create Offer
                        </Typography>
                        <CleanupImagesButton />
                    </Box>

                    {/* Dates Section */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 3,
                            mb: 4,
                        }}
                    >
                        <TextField
                            label="From Date"
                            type="datetime-local"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            error={!!errors.fromDate}
                            helperText={errors.fromDate}
                            fullWidth
                        />

                        <TextField
                            label="To Date"
                            type="datetime-local"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            error={!!errors.toDate}
                            helperText={errors.toDate}
                            fullWidth
                        />
                    </Box>

                    {/* File Upload */}
                    <Box sx={{ mb: 3 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                px: 2.5,
                                py: 1,
                                "&:hover": {
                                    borderColor: "#1976d2",
                                    background: "#f0f7ff",
                                },
                            }}
                        >
                            📷 Select Images
                            <input
                                type="file"
                                hidden
                                multiple
                                accept="image/*"
                                onChange={handleImageChange}
                            />
                        </Button>
                        {errors.images && (
                            <Typography
                                color="error"
                                variant="caption"
                                sx={{ display: "block", mt: 1 }}
                            >
                                {errors.images}
                            </Typography>
                        )}
                    </Box>

                    {/* Image Previews */}
                    {images.length > 0 && (
                        <Box
                            sx={{
                                mb: 3,
                                p: 2,
                                bgcolor: "#fafafa",
                                borderRadius: 2,
                                border: "1px solid #e0e0e0",
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{ mb: 2, color: "#555", fontWeight: 500 }}
                            >
                                Selected Images ({images.length})
                            </Typography>
                            <Grid container spacing={1.5}>
                                {images.map((img, idx) => (
                                    <Grid item key={idx}>
                                        <Box
                                            sx={{
                                                position: "relative",
                                                width: 95,
                                                height: 95,
                                                border: "2px solid #ddd",
                                                borderRadius: 2,
                                                overflow: "hidden",
                                                transition: "all 0.25s ease",
                                                "&:hover": {
                                                    borderColor: "#1976d2",
                                                    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                                                },
                                            }}
                                        >
                                            <img
                                                src={URL.createObjectURL(img)}
                                                alt={`preview ${idx + 1}`}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    position: "absolute",
                                                    top: 2,
                                                    right: 2,
                                                    background: "#fff",
                                                    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                                                    "&:hover": {
                                                        background: "#ffebee",
                                                        color: "#d32f2f",
                                                    },
                                                }}
                                                onClick={() => handleDeleteImage(idx)}
                                            >
                                                <Delete sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {/* Footer Buttons */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 2,
                            pt: 2,
                            borderTop: "1px solid #e0e0e0",
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="secondary"
                            size="medium"
                            onClick={() => {
                                setFromDate("");
                                setToDate("");
                                setImages([]);
                                setErrors({});
                            }}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                px: 3,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            onClick={handleSubmit}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                px: 3,
                            }}
                        >
                            Submit
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );

};

export default OfferPage;
